import React from "react";
import { DeviceIcon } from "@/components/DeviceIcon";
import DashboardWidget from "@/components/DashboardWidget";
import { Apple, Monitor } from "lucide-react";

interface AnalyticsStats {
  trafficSources: {
    utm: Record<string, number>;
  };
  topDevicesBySource: Record<
    string,
    { type: string; os: string; count: number; percentage: number }
  >;
  deviceBreakdownBySource: Record<
    string,
    Array<{ type: string; os: string; count: number }>
  >;
}

interface TopSourceCategoriesWidgetProps {
  stats: AnalyticsStats | null;
}

export const TopSourceCategoriesWidget: React.FC<
  TopSourceCategoriesWidgetProps
> = ({ stats }) => {
  const utmSources = stats?.trafficSources.utm || {};
  const totalUTMVisitors = Object.values(utmSources).reduce((a, b) => a + b, 0);

  // Define source categories
  const categories = {
    "Social Media Sources": ["facebook", "instagram", "whatsapp"],
    "Google Sources": ["google_organic", "google_maps"],
    "Physical Marketing Sources": [
      "business_card",
      "poster",
      "car_door",
      "window_sticker",
      "flyer",
    ],
    "Website Sources": ["website"],
    "Ads Sources": [
      "facebook_ads",
      "instagram_ads",
      "tiktok_ads",
      "whatsapp_ads",
      "google_ads",
      "google_maps_ads",
    ],
  };

  const categoryColors = {
    "Social Media Sources": "from-pink-500 to-rose-500",
    "Google Sources": "from-green-500 to-emerald-500",
    "Physical Marketing Sources": "from-orange-500 to-amber-500",
    "Website Sources": "from-blue-500 to-indigo-500",
    "Ads Sources": "from-purple-500 to-violet-500",
  };

  const categoryIcons = {
    "Social Media Sources": "👥",
    "Google Sources": "🔍",
    "Physical Marketing Sources": "📄",
    "Website Sources": "🌐",
    "Ads Sources": "🎯",
  };

  return (
    <DashboardWidget
      id="top-source-categories"
      title="Top Performing Source Categories"
    >
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {Object.entries(categories).map(([categoryName, sources]) => {
          const totalVisitors = sources.reduce(
            (sum, source) => sum + (utmSources[source] || 0),
            0,
          );
          const percentage =
            totalUTMVisitors > 0
              ? ((totalVisitors / totalUTMVisitors) * 100).toFixed(1)
              : "0";

          return (
            <div
              key={categoryName}
              className="text-center p-4 rounded-lg border bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="text-2xl mb-2">
                {categoryIcons[categoryName as keyof typeof categoryIcons]}
              </div>
              <h3 className="font-semibold text-sm text-gray-900 mb-2">
                {categoryName}
              </h3>
              <div
                className={`text-2xl font-bold bg-gradient-to-r ${categoryColors[categoryName as keyof typeof categoryColors]} bg-clip-text text-transparent mb-1`}
              >
                {totalVisitors}
              </div>
              <div className="text-xs text-gray-600">
                {percentage}% of UTM traffic
              </div>

              {/* Show all sources in this category with device breakdown */}
              <div className="mt-3 space-y-2">
                {sources
                  .map((source) => ({
                    source,
                    count: utmSources[source] || 0,
                  }))
                  .sort((a, b) => b.count - a.count) // Sort by count (highest first)
                  .map(({ source, count }) => {
                    const sourceLabels: Record<string, string> = {
                      website: "Website",
                      facebook: "Facebook",
                      facebook_ads: "FB Ads",
                      instagram: "Instagram",
                      instagram_ads: "IG Ads",
                      tiktok_ads: "TikTok Ads",
                      google_organic: "Google",
                      google_ads: "Google Ads",
                      google_maps: "Maps",
                      google_maps_ads: "Maps Ads",
                      whatsapp: "WhatsApp",
                      whatsapp_ads: "WA Ads",
                      business_card: "Business Card",
                      poster: "Poster",
                      car_door: "Car Door",
                      window_sticker: "Window",
                      flyer: "Flyer",
                    };

                    const label = sourceLabels[source] || source;
                    const devices =
                      stats?.deviceBreakdownBySource?.[source] || [];

                    // Count devices by type
                    const appleCount = devices
                      .filter(
                        (d) =>
                          d.os.toLowerCase().includes("ios") ||
                          d.os.toLowerCase().includes("mac") ||
                          d.os.toLowerCase().includes("iphone") ||
                          d.os.toLowerCase().includes("ipad"),
                      )
                      .reduce((sum, d) => sum + d.count, 0);

                    const androidCount = devices
                      .filter((d) => d.os.toLowerCase().includes("android"))
                      .reduce((sum, d) => sum + d.count, 0);

                    const windowsCount = devices
                      .filter(
                        (d) =>
                          d.os.toLowerCase().includes("windows") ||
                          d.os.toLowerCase().includes("win"),
                      )
                      .reduce((sum, d) => sum + d.count, 0);

                    const othersCount = devices
                      .filter((d) => {
                        const osLower = d.os.toLowerCase();
                        return (
                          !osLower.includes("ios") &&
                          !osLower.includes("mac") &&
                          !osLower.includes("android") &&
                          !osLower.includes("windows") &&
                          !osLower.includes("win") &&
                          !osLower.includes("iphone") &&
                          !osLower.includes("ipad")
                        );
                      })
                      .reduce((sum, d) => sum + d.count, 0);

                    return (
                      <div
                        key={source}
                        className={`border rounded-md p-2 mb-2 ${count === 0 ? "bg-gray-50 text-gray-400" : "bg-white text-gray-700"}`}
                      >
                        <div
                          className={`flex justify-between items-center text-xs mb-1 ${count === 0 ? "text-gray-400" : "text-gray-600"}`}
                        >
                          <span
                            className={`font-medium ${count === 0 ? "text-gray-400" : "text-gray-700"} truncate`}
                          >
                            {label}
                          </span>
                          <span
                            className={`font-bold ${count === 0 ? "text-gray-400" : "text-gray-800"}`}
                          >
                            {count}
                          </span>
                        </div>

                        {/* Device breakdown */}
                        {count > 0 && (
                          <div className="flex justify-between items-center text-xs">
                            <div className="flex items-center gap-1">
                              <Apple className="h-2.5 w-2.5" />
                              <span className="text-gray-600">
                                {appleCount}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <svg
                                className="h-2.5 w-2.5"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M17.523 15.3414c-.5665 0-1.0253-.4588-1.0253-1.0253s.4588-1.0253 1.0253-1.0253 1.0253.4588 1.0253 1.0253-.4588 1.0253-1.0253 1.0253zm-11.046 0c-.5665 0-1.0253-.4588-1.0253-1.0253s.4588-1.0253 1.0253-1.0253 1.0253.4588 1.0253 1.0253-.4588 1.0253-1.0253 1.0253zm11.405-6.02l1.395-2.4157c.077-.1326.0315-.3017-.1011-.3787-.1326-.077-.3017-.0315-.3787.1011L17.4042 8.8495c-.6897-.3157-1.4612-.4912-2.4042-.4912s-1.7145.1755-2.4042.4912L11.208 6.6671c-.077-.1326-.2461-.1781-.3787-.1011-.1326.077-.1781.2461-.1011.3787l1.395 2.4157C10.0618 10.2971 8.535 11.7842 8.535 13.6253v.8439h6.93v-.8439c0-1.8411-1.5268-3.3282-3.465-4.2839zM24 12c0 6.6274-5.3726 12-12 12S0 18.6274 0 12 5.3726 0 12 0s12 5.3726 12 12z" />
                              </svg>
                              <span className="text-gray-600">
                                {androidCount}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <svg
                                className="h-2.5 w-2.5"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-13.051-1.351" />
                              </svg>
                              <span className="text-gray-600">
                                {windowsCount}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Monitor className="h-2.5 w-2.5" />
                              <span className="text-gray-600">
                                {othersCount}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>
    </DashboardWidget>
  );
};

export default TopSourceCategoriesWidget;
